import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { fetchEventByMeetupNo } from "../api/fetchEventByMeetupNo";
import { downloadZip } from "../utils/downloadZip";
import PromoControls from "../Components/PromoMaterials/PromoControls";
import PromoCard from "../Components/PromoMaterials/PromoCard";
import InstagramTitle from "../Components/PromoMaterials/materials/InstagramTitle";
import InstagramSpeaker from "../Components/PromoMaterials/materials/InstagramSpeaker";
import InstagramDetails from "../Components/PromoMaterials/materials/InstagramDetails";
import Banner from "../Components/PromoMaterials/materials/Banner";
import SlackBanner from "../Components/PromoMaterials/materials/SlackBanner";
import FacebookBanner from "../Components/PromoMaterials/materials/FacebookBanner";
import SignEventTitle from "../Components/PromoMaterials/materials/SignEventTitle";
import SignEditable from "../Components/PromoMaterials/materials/SignEditable";
import SoMePhotoFrame from "../Components/PromoMaterials/materials/SoMePhotoFrame";
import styles from "../Components/PromoMaterials/PromoMaterials.module.scss";
import "../styles/pages/promoPage.scss";

const getTextColor = (color) => {
  if (["ixda-red", "ixda-purple", "gray-1"].includes(color)) {
    return "#ffffff";
  }
  return "#333333";
};

const getBgColor = (color) => {
  const colorMap = {
    "ixda-red": "var(--ixda-red)",
    "ixda-purple": "var(--ixda-purple)",
    "ixda-blue": "var(--ixda-blue)",
    "ixda-green": "var(--ixda-green)",
    "ixda-orange": "var(--ixda-orange)",
    "ixda-pink": "var(--ixda-pink)",
    "ixda-cyan": "var(--ixda-cyan)",
    "ixda-yellow": "var(--ixda-yellow)",
    "gray-1": "var(--gray-1)",
    white: "var(--white)",
  };
  return colorMap[color] || "var(--ixda-yellow)";
};

const ARROW_LABELS = {
  up: "\u2191",
  right: "\u2192",
  down: "\u2193",
  left: "\u2190",
};

const BANNER_FONT_DEFAULTS = {
  bannerTitle: 120,
  bannerSpeaker: 60,
  slackTitle: 120,
  fbTitle: 220,
  fbSpeaker: 120,
};

const FONT_STEP = 4;

export const PromoPage = () => {
  const { meetupNo } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [format, setFormat] = useState("png");
  const [downloading, setDownloading] = useState(false);
  const [editText, setEditText] = useState("");
  const [editArrow, setEditArrow] = useState("right");
  const [somePhotos, setSomePhotos] = useState([]);
  const [someDownloading, setSomeDownloading] = useState(false);
  const [bannerTitleSize, setBannerTitleSize] = useState(BANNER_FONT_DEFAULTS.bannerTitle);
  const [bannerSpeakerSize, setBannerSpeakerSize] = useState(BANNER_FONT_DEFAULTS.bannerSpeaker);
  const [slackTitleSize, setSlackTitleSize] = useState(BANNER_FONT_DEFAULTS.slackTitle);
  const [fbTitleSize, setFbTitleSize] = useState(BANNER_FONT_DEFAULTS.fbTitle);
  const [fbSpeakerSize, setFbSpeakerSize] = useState(BANNER_FONT_DEFAULTS.fbSpeaker);

  // Refs for all materials
  const igTitleRef = useRef(null);
  const igDetailsRef = useRef(null);
  const meetupRef = useRef(null);
  const linkedinRef = useRef(null);
  const emailRef = useRef(null);
  const slackRef = useRef(null);
  const facebookRef = useRef(null);
  const signTitleRef = useRef(null);
  const signEditableRef = useRef(null);
  const speakerRefs = useRef([]);
  const somePhotoRefs = useRef([]);
  const someFileInputRef = useRef(null);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");

    html.style.overflow = "auto";
    html.style.position = "static";
    html.style.height = "auto";
    body.style.overflow = "auto";
    body.style.position = "static";
    body.style.height = "auto";
    body.style.overscrollBehaviorY = "auto";
    if (root) {
      root.style.overflow = "auto";
      root.style.position = "static";
      root.style.height = "auto";
    }

    return () => {
      html.style.overflow = "";
      html.style.position = "";
      html.style.height = "";
      body.style.overflow = "";
      body.style.position = "";
      body.style.height = "";
      body.style.overscrollBehaviorY = "";
      if (root) {
        root.style.overflow = "";
        root.style.position = "";
        root.style.height = "";
      }
    };
  }, []);

  useEffect(() => {
    const loadEvent = async () => {
      setLoading(true);
      const data = await fetchEventByMeetupNo(meetupNo);
      if (data) {
        setEvent(data);
        speakerRefs.current = (data.speakers || []).map(() => React.createRef());
      } else {
        setError(`Meetup #${meetupNo} not found`);
      }
      setLoading(false);
    };
    loadEvent();
  }, [meetupNo]);


  const handleSomeUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    // Use data URLs so html-to-image can embed them during capture
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const photo = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          dataUrl: reader.result,
          file,
          crop: { scale: 1, offsetX: 0, offsetY: 0 },
        };
        setSomePhotos((prev) => {
          const updated = [...prev, photo];
          somePhotoRefs.current = updated.map((_, i) => somePhotoRefs.current[i] || React.createRef());
          return updated;
        });
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  }, []);

  const handleSomeRemove = useCallback((id) => {
    setSomePhotos((prev) => {
      const updated = prev.filter((p) => p.id !== id);
      somePhotoRefs.current = updated.map((_, i) => somePhotoRefs.current[i] || React.createRef());
      return updated;
    });
  }, []);

  const handleSomeCropChange = useCallback((id, crop) => {
    setSomePhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, crop } : p))
    );
  }, []);

  const handleDownloadAllSome = useCallback(async () => {
    if (!somePhotos.length) return;
    setSomeDownloading(true);
    const refs = somePhotos.map((photo, i) => ({
      name: `meetup-${meetupNo}-some-photo-${i + 1}`,
      ref: somePhotoRefs.current[i],
      width: 1080,
      height: 1350,
    }));
    try {
      await downloadZip(refs, `${meetupNo}-some-photos`, format);
    } catch (err) {
      console.error("Failed to generate SoMe ZIP:", err);
    }
    setSomeDownloading(false);
  }, [somePhotos, meetupNo, format]);

  const handleDownloadAll = useCallback(async () => {
    if (!event) return;
    setDownloading(true);

    const materialRefs = [
      { name: `meetup-${meetupNo}-ig-title`, ref: igTitleRef, width: 1080, height: 1350 },
      ...speakerRefs.current.map((ref, i) => ({
        name: `meetup-${meetupNo}-ig-speaker-${i + 1}`,
        ref,
        width: 1080,
        height: 1350,
      })),
      { name: `meetup-${meetupNo}-ig-details`, ref: igDetailsRef, width: 1080, height: 1350 },
      { name: `meetup-${meetupNo}-meetup-banner`, ref: meetupRef, width: 1920, height: 1080 },
      { name: `meetup-${meetupNo}-linkedin-banner`, ref: linkedinRef, width: 1920, height: 1080 },
      { name: `meetup-${meetupNo}-email-banner`, ref: emailRef, width: 1920, height: 1080 },
      { name: `meetup-${meetupNo}-slack-banner`, ref: slackRef, width: 2361, height: 1669 },
      { name: `meetup-${meetupNo}-facebook-banner`, ref: facebookRef, width: 3840, height: 2010 },
      { name: `meetup-${meetupNo}-sign-title`, ref: signTitleRef, width: 2370, height: 1675 },
      { name: `meetup-${meetupNo}-sign-editable`, ref: signEditableRef, width: 2370, height: 1675 },
    ];

    try {
      await downloadZip(materialRefs, meetupNo, format);
    } catch (err) {
      console.error("Failed to generate ZIP:", err);
    }
    setDownloading(false);
  }, [event, format, meetupNo]);

  const adjustSize = (setter, current, delta) => {
    setter(Math.max(FONT_STEP, current + delta));
  };

  const renderSizeField = (label, current, defaultValue, setter) => {
    const changed = current !== defaultValue;
    return (
      <div className={styles.fontSizeField}>
        <span className={styles.fontSizeFieldLabel}>{label}:</span>
        <button
          type="button"
          className={styles.fontSizeStepBtn}
          onClick={() => adjustSize(setter, current, -FONT_STEP)}
          disabled={current <= FONT_STEP}
          aria-label={`Decrease ${label}`}
        >
          −
        </button>
        <span className={`${styles.fontSizeReadout} ${changed ? styles.fontSizeReadoutChanged : ""}`}>
          {changed ? `${defaultValue} → ${current}px` : `${current}px`}
        </span>
        <button
          type="button"
          className={styles.fontSizeStepBtn}
          onClick={() => adjustSize(setter, current, FONT_STEP)}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    );
  };

  const resetBannerGroup = () => {
    setBannerTitleSize(BANNER_FONT_DEFAULTS.bannerTitle);
    setBannerSpeakerSize(BANNER_FONT_DEFAULTS.bannerSpeaker);
  };
  const resetSlackGroup = () => {
    setSlackTitleSize(BANNER_FONT_DEFAULTS.slackTitle);
  };
  const resetFbGroup = () => {
    setFbTitleSize(BANNER_FONT_DEFAULTS.fbTitle);
    setFbSpeakerSize(BANNER_FONT_DEFAULTS.fbSpeaker);
  };

  const bannerGroupChanged =
    bannerTitleSize !== BANNER_FONT_DEFAULTS.bannerTitle ||
    bannerSpeakerSize !== BANNER_FONT_DEFAULTS.bannerSpeaker;
  const slackGroupChanged = slackTitleSize !== BANNER_FONT_DEFAULTS.slackTitle;
  const fbGroupChanged =
    fbTitleSize !== BANNER_FONT_DEFAULTS.fbTitle ||
    fbSpeakerSize !== BANNER_FONT_DEFAULTS.fbSpeaker;

  if (loading) {
    return <div className={styles.loading}>Loading Meetup #{meetupNo}...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  const textColor = getTextColor(event.color);
  const bgColor = getBgColor(event.color);
  const bannerProps = { event, textColor, bgColor };

  return (
    <div className="promo-page">
      <PromoControls
        format={format}
        onFormatChange={setFormat}
        onDownloadAll={handleDownloadAll}
        downloading={downloading}
      />

      <div className={styles.pageHeader}>
        <h1>Meetup #{event.eventMeetupNo} — {event.eventName}</h1>
        <p>Promo materials for all platforms. Download individually or as a ZIP.</p>
      </div>

      <div className={styles.materialsGrid}>
        {/* Instagram — uses event color */}
        <div className={styles.section}>
          <h2>Instagram</h2>
          <div className={styles.sectionCards}>
            <PromoCard
              label="Title Slide"
              filename={`meetup-${meetupNo}-ig-title`}
              format={format}
              width={1080}
              height={1350}
              captureRef={igTitleRef}
            >
              <InstagramTitle {...bannerProps} />
            </PromoCard>

            {event.speakers?.map((speaker, i) => (
              <PromoCard
                key={i}
                label={`Speaker — ${speaker.name}`}
                filename={`meetup-${meetupNo}-ig-speaker-${i + 1}`}
                format={format}
                width={1080}
                height={1350}
                captureRef={speakerRefs.current[i]}
              >
                <InstagramSpeaker speaker={speaker} {...bannerProps} />
              </PromoCard>
            ))}

            <PromoCard
              label="Details Slide"
              filename={`meetup-${meetupNo}-ig-details`}
              format={format}
              width={1080}
              height={1350}
              captureRef={igDetailsRef}
            >
              <InstagramDetails {...bannerProps} />
            </PromoCard>
          </div>
        </div>

        {/* Banners — use event color */}
        <div className={styles.section}>
          <h2>Banners</h2>
          <div className={styles.fontSizeControls}>
            <div className={styles.fontSizeRow}>
              <span className={styles.fontSizeLabel}>Meetup / LinkedIn / Email</span>
              {renderSizeField("Title", bannerTitleSize, BANNER_FONT_DEFAULTS.bannerTitle, setBannerTitleSize)}
              {renderSizeField("Speakers", bannerSpeakerSize, BANNER_FONT_DEFAULTS.bannerSpeaker, setBannerSpeakerSize)}
              <button
                type="button"
                className={styles.fontSizeResetBtn}
                onClick={resetBannerGroup}
                disabled={!bannerGroupChanged}
              >
                Reset
              </button>
            </div>
            <div className={styles.fontSizeRow}>
              <span className={styles.fontSizeLabel}>Slack / UX Norge</span>
              {renderSizeField("Title", slackTitleSize, BANNER_FONT_DEFAULTS.slackTitle, setSlackTitleSize)}
              <button
                type="button"
                className={styles.fontSizeResetBtn}
                onClick={resetSlackGroup}
                disabled={!slackGroupChanged}
              >
                Reset
              </button>
            </div>
            <div className={styles.fontSizeRow}>
              <span className={styles.fontSizeLabel}>Facebook</span>
              {renderSizeField("Title", fbTitleSize, BANNER_FONT_DEFAULTS.fbTitle, setFbTitleSize)}
              {renderSizeField("Speakers", fbSpeakerSize, BANNER_FONT_DEFAULTS.fbSpeaker, setFbSpeakerSize)}
              <button
                type="button"
                className={styles.fontSizeResetBtn}
                onClick={resetFbGroup}
                disabled={!fbGroupChanged}
              >
                Reset
              </button>
            </div>
          </div>
          <div className={styles.sectionCards}>
            <PromoCard
              label="Meetup.com"
              filename={`meetup-${meetupNo}-meetup-banner`}
              format={format}
              width={1920}
              height={1080}
              captureRef={meetupRef}
            >
              <Banner
                {...bannerProps}
                variant="meetup"
                titleFontSize={bannerTitleSize}
                speakerFontSize={bannerSpeakerSize}
              />
            </PromoCard>

            <PromoCard
              label="LinkedIn"
              filename={`meetup-${meetupNo}-linkedin-banner`}
              format={format}
              width={1920}
              height={1080}
              captureRef={linkedinRef}
            >
              <Banner
                {...bannerProps}
                variant="linkedin"
                titleFontSize={bannerTitleSize}
                speakerFontSize={bannerSpeakerSize}
              />
            </PromoCard>

            <PromoCard
              label="Email"
              filename={`meetup-${meetupNo}-email-banner`}
              format={format}
              width={1920}
              height={1080}
              captureRef={emailRef}
            >
              <Banner
                {...bannerProps}
                variant="email"
                titleFontSize={bannerTitleSize}
                speakerFontSize={bannerSpeakerSize}
              />
            </PromoCard>

            <PromoCard
              label="Slack / UX Norge"
              filename={`meetup-${meetupNo}-slack-banner`}
              format={format}
              width={2361}
              height={1669}
              captureRef={slackRef}
            >
              <SlackBanner {...bannerProps} titleFontSize={slackTitleSize} />
            </PromoCard>

            <PromoCard
              label="Facebook"
              filename={`meetup-${meetupNo}-facebook-banner`}
              format={format}
              width={3840}
              height={2010}
              captureRef={facebookRef}
            >
              <FacebookBanner
                {...bannerProps}
                titleFontSize={fbTitleSize}
                speakerFontSize={fbSpeakerSize}
              />
            </PromoCard>
          </div>
        </div>

        {/* A4 Signs */}
        <div className={styles.section}>
          <h2>A4 Signs</h2>
          <div className={styles.sectionCards}>
            <PromoCard
              label="Event Title"
              filename={`meetup-${meetupNo}-sign-title`}
              format={format}
              width={2370}
              height={1675}
              captureRef={signTitleRef}
            >
              <SignEventTitle {...bannerProps} />
            </PromoCard>

            <div>
              {/* Edit controls outside the capture area */}
              <div className={styles.editControls} style={{ marginBottom: 8 }}>
                <input
                  type="text"
                  placeholder="Enter sign text..."
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className={styles.editInput}
                />
                <div className={styles.arrowButtons}>
                  {Object.entries(ARROW_LABELS).map(([dir, arrow]) => (
                    <button
                      key={dir}
                      className={`${styles.arrowBtn} ${editArrow === dir ? styles.arrowBtnActive : ""}`}
                      onClick={() => setEditArrow(dir)}
                    >
                      {arrow}
                    </button>
                  ))}
                </div>
              </div>
              <PromoCard
                label="Editable Sign"
                filename={`meetup-${meetupNo}-sign-editable`}
                format={format}
                width={2370}
                height={1675}
                captureRef={signEditableRef}
              >
                <SignEditable
                  {...bannerProps}
                  editText={editText}
                  editArrow={editArrow}
                />
              </PromoCard>
            </div>
          </div>
        </div>

        {/* SoMe Photos */}
        <div className={styles.section}>
          <h2>SoMe Photos</h2>
          {somePhotos.length > 0 && (
            <div className={styles.someControls}>
              <button onClick={handleDownloadAllSome} disabled={someDownloading}>
                {someDownloading ? "Zipping..." : "Download all SoMe"}
              </button>
            </div>
          )}
          <div
            className={styles.someUploadZone}
            onClick={() => someFileInputRef.current?.click()}
          >
            <input
              ref={someFileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleSomeUpload}
            />
            Click or drag photos here to add SoMe frames
          </div>
          {somePhotos.length > 0 && (
            <p className={styles.someHint}>Drag to reposition, scroll to zoom. Images are stored locally, so if you refresh or close the page they will be lost. Download individually or all at once.</p>
          )}
          {somePhotos.length > 0 && (
            <div className={styles.sectionCards} style={{ marginTop: 16 }}>
              {somePhotos.map((photo, i) => {
                if (!somePhotoRefs.current[i]) {
                  somePhotoRefs.current[i] = React.createRef();
                }
                return (
                  <div key={photo.id}>
                    <PromoCard
                      label={`Photo ${i + 1}`}
                      filename={`meetup-${meetupNo}-some-photo-${i + 1}`}
                      format={format}
                      width={1080}
                      height={1350}
                      captureRef={somePhotoRefs.current[i]}
                    >
                      <SoMePhotoFrame
                        imageUrl={photo.dataUrl}
                        event={event}
                        bgColor={bgColor}
                        crop={photo.crop}
                        onCropChange={(crop) => handleSomeCropChange(photo.id, crop)}
                      />
                    </PromoCard>
                    <button
                      className={styles.someRemoveBtn}
                      onClick={() => handleSomeRemove(photo.id)}
                      style={{ marginTop: 4 }}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
